import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { ReferenceMode } from 'containers/SubSidebar/AIVideoContent/consts'
import { ReferenceMediaImageType } from 'types/aiVideo'
import { createReferenceMediaItem } from 'store/features/aiVideo/utils'
import { IMyFilesMediaItem } from 'types/media'
import {
  useAiVideoFileValidator,
  useMediaLibValidator
} from '../../hooks/useAiVideoUploadUtils'

const dispatchUploadMultiRef = async (file: File) => {
  const uploadItem = {
    id: crypto.randomUUID(),
    type: 'image' as const,
    file
  }
  await dispatch(aiVideoThunks.uploadMultiRefImage([uploadItem]))
}

export const useMultiRefUpload = () => {
  const { createUploadHandler } = useAiVideoFileValidator()
  const { validateMediaLibFiles } = useMediaLibValidator()

  const multiRefImagesBucket = useSelector(
    aiVideoSelectors.multiRefImagesBucket
  )
  const multiRefMaxImageCount = useSelector(
    aiVideoSelectors.multiRefMaxImageCount
  )
  const isUploading = useSelector(aiVideoSelectors.isAnyRefImageUploading)

  const availableCount = multiRefMaxImageCount
    ? multiRefMaxImageCount - multiRefImagesBucket.length
    : Infinity

  const handleFilesUpload = createUploadHandler((validFiles: File[]) => {
    const filesToUpload = validFiles.slice(0, Math.max(0, availableCount))
    for (const file of filesToUpload) {
      dispatchUploadMultiRef(file)
    }
  })

  const handleMediaLibFiles = useCallback(
    (files: IMyFilesMediaItem[]) => {
      const valid = validateMediaLibFiles(files)
      if (!valid) return

      const filesToAdd = valid.slice(0, Math.max(0, availableCount))
      for (const file of filesToAdd) {
        const item = createReferenceMediaItem(
          ReferenceMediaImageType.REFERENCE_IMAGE,
          file.filePath
        )
        dispatch(
          aiVideoThunks.addReferenceMediaItem({
            item,
            bucket: ReferenceMode.IMAGES
          })
        )
      }
    },
    [validateMediaLibFiles, availableCount]
  )

  const handleRemoveImage = useCallback((id: string) => {
    dispatch(
      aiVideoThunks.removeReferenceMediaItem({
        id,
        bucket: ReferenceMode.IMAGES
      })
    )
  }, [])

  return {
    multiRefImagesBucket,
    availableCount,
    isUploading,
    handleFilesUpload,
    handleMediaLibFiles,
    handleRemoveImage
  }
}
